import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Truck, Bath, Heart, ChevronDown } from "lucide-react";

const STEPS = [
  { id: "step-1", icon: CalendarCheck, title: "Book Appointment", desc: "Pick your preferred pet service, date & slot time in seconds with zero pre-payment.", color: "bg-woofsy-primary/15 text-woofsy-primary" },
  { id: "step-2", icon: Truck, title: "Groomer Arrives", desc: "Our fully equipped mobile spa van reaches your Pune doorstep within ~30 minutes.", color: "bg-woofsy-secondary/25 text-sky-600" },
  { id: "step-3", icon: Bath, title: "Pet Gets Groomed", desc: "Spa-grade grooming, warm hydro-bath & herbal massage inside our climate-controlled van or home.", color: "bg-woofsy-accent/25 text-green-600" },
  { id: "step-4", icon: Heart, title: "Happy Pet", desc: "A fresh, clean, fluffy & joyful pet returned right back to your loving hands!", color: "bg-pink-100 text-pink-500" },
];

export default function HowItWorks() {
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (id) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="how" data-testid="how-it-works" className="relative py-24 sm:py-32 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-woofsy-primary mb-3 flex items-center gap-1.5">
            <span className="animate-tail-wag">🐶</span>
            <span>How It Works</span>
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-5xl text-woofsy-dark">Grooming, in four happy little steps.</h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => {
            const isExpanded = expandedCards[s.id] || false;
            return (
              <motion.div key={s.id} data-testid={`step-card-${i}`}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.12, type: "spring", stiffness: 90 }}
                whileHover={{ y: -8 }}
                className="relative glass rounded-3xl p-7 shadow-[0_10px_40px_rgba(28,36,48,0.06)] hover:shadow-[0_20px_50px_rgba(255,138,61,0.18)] transition-all flex flex-col justify-between group">
                <span className="absolute top-5 right-6 font-heading font-extrabold text-5xl text-woofsy-dark/5">{i + 1}</span>

                <div>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${s.color} mb-5 group-hover:scale-110 transition-transform`}>
                    <s.icon className="w-7 h-7 animate-ear-wiggle" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-woofsy-dark mb-2">{s.title}</h3>
                  <p className={`text-woofsy-dark/60 text-sm leading-relaxed transition-all ${isExpanded ? "" : "line-clamp-2"}`}>
                    {s.desc}
                  </p>
                </div>

                {/* Bottom Right Down Arrow Toggle */}
                <div className="mt-5 pt-3 flex justify-end border-t border-slate-100/60">
                  <button
                    onClick={() => toggleCard(s.id)}
                    data-testid={`down-arrow-step-${i}`}
                    className="flex items-center gap-1 text-[11px] font-bold text-woofsy-primary hover:text-woofsy-dark px-2.5 py-1 rounded-full bg-orange-50/80 border border-orange-100 shadow-sm transition-all"
                    title={isExpanded ? "Collapse step details" : "Expand step details"}
                  >
                    <span>{isExpanded ? "Less" : "More"}</span>
                    <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </motion.div>
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
